import type { LimitRule, TaxProfile } from './types';

/**
 * Mock reference data that the real system serves from PayrollConfig: read-only tax profiles and limit rules (contract section 4.2).
 *
 * The real system seeds STRUCTURAL DRAFT profiles only, never Approved, with every state and local cell `ReviewRequired`, and the limit rule seed
 * rows carry NO amount (no number is invented or hard-coded). Only the non-production synthetic seeder may insert an Approved profile, and it must
 * mark it synthetic. The mock mirrors exactly that: four Draft profiles with the official source pages, two SYNTHETIC Approved profiles so a demo can
 * show a Complete plan, and Draft limit rules with no amount.
 */

const SOURCES = {
  pub15: { sourceTitle: 'IRS Publication 15', sourceUrl: 'https://www.irs.gov/publications/p15' },
  pub15b: { sourceTitle: 'IRS Publication 15-B', sourceUrl: 'https://www.irs.gov/publications/p15b' },
  retirement: { sourceTitle: 'IRS retirement plan limits', sourceUrl: 'https://www.irs.gov/retirement-plans/cola-increases-for-dollar-limitations-on-benefits-and-contributions' },
  dol: { sourceTitle: 'Department of Labor wage garnishment guidance', sourceUrl: 'https://www.dol.gov/agencies/whd/wage-garnishment' },
};

const REVIEW: TaxProfile['treatments'] = {
  federalIncomeTax: 'ReviewRequired', socialSecurity: 'ReviewRequired', medicare: 'ReviewRequired', futa: 'ReviewRequired',
  stateIncomeTax: 'ReviewRequired', stateUnemployment: 'ReviewRequired', localIncomeTax: 'ReviewRequired', otherBases: 'ReviewRequired',
};

function draft(code: string, name: string, description: string, source: { sourceTitle: string; sourceUrl: string }): TaxProfile {
  return {
    code, name, description, version: 1, effectiveFrom: null, effectiveTo: null, status: 'Draft', approvedForPayroll: false, w2Box: null, w2Code: null,
    sourceTitle: source.sourceTitle, sourceUrl: source.sourceUrl, isSynthetic: false, treatments: { ...REVIEW },
  };
}

function approvedSynthetic(code: string, name: string, description: string, source: { sourceTitle: string; sourceUrl: string }): TaxProfile {
  return {
    code, name, description, version: 1, effectiveFrom: '2000-01-01', effectiveTo: null, status: 'Approved', approvedForPayroll: true, w2Box: null, w2Code: null,
    sourceTitle: source.sourceTitle, sourceUrl: source.sourceUrl, isSynthetic: true,
    treatments: {
      federalIncomeTax: 'NotApplicable', socialSecurity: 'NotApplicable', medicare: 'NotApplicable', futa: 'NotApplicable',
      stateIncomeTax: 'NotApplicable', stateUnemployment: 'NotApplicable', localIncomeTax: 'NotApplicable', otherBases: 'NotApplicable',
    },
  };
}

export const MOCK_TAX_PROFILES: readonly TaxProfile[] = [
  draft('DRAFT-PRETAX-CAFETERIA', 'Pre-tax benefit premium (structural draft)', 'A structural draft. Not reviewed or approved for payroll.', SOURCES.pub15b),
  draft('DRAFT-PRETAX-RETIREMENT', 'Pre-tax retirement contribution (structural draft)', 'A structural draft. Not reviewed or approved for payroll.', SOURCES.pub15),
  draft('DRAFT-POSTTAX-NO-WAGE-EFFECT', 'Post-tax deduction (structural draft)', 'A structural draft. Not reviewed or approved for payroll.', SOURCES.pub15),
  draft('DRAFT-GARNISHMENT-WITHHOLDING', 'Garnishment withholding (structural draft)', 'A structural draft. Not reviewed or approved for payroll.', SOURCES.dol),
  approvedSynthetic('SYNTHETIC-BENEFIT-PROFILE', 'SYNTHETIC benefit profile (demo only)', 'A synthetic approved profile for the demo company. Not a real tax treatment.', SOURCES.pub15b),
  approvedSynthetic('SYNTHETIC-POSTTAX-PROFILE', 'SYNTHETIC post-tax profile (demo only)', 'A synthetic approved profile for the demo company. Not a real tax treatment.', SOURCES.pub15),
];

function limitRule(code: string, name: string, unit: string): LimitRule {
  return { code, name, version: 1, effectiveFrom: null, effectiveTo: null, amount: null, unit, ageThreshold: null, status: 'Draft', isSynthetic: false };
}

/** Seed rows carry no amount and status Draft: nothing is invented. */
export const MOCK_LIMIT_RULES: readonly LimitRule[] = [
  limitRule('LIMIT-401K-EMPLOYEE-DEFERRAL', '401(k) employee deferral limit', 'USD per year'),
  limitRule('LIMIT-401K-CATCH-UP', '401(k) catch-up limit', 'USD per year'),
  limitRule('LIMIT-HSA-SELF-ONLY', 'HSA limit, self-only coverage', 'USD per year'),
  limitRule('LIMIT-HSA-FAMILY', 'HSA limit, family coverage', 'USD per year'),
  limitRule('LIMIT-HEALTHCARE-FSA', 'Healthcare FSA limit', 'USD per year'),
  limitRule('LIMIT-DEPENDENT-CARE-FSA', 'Dependent care FSA limit', 'USD per year'),
  limitRule('LIMIT-COMMUTER-TRANSIT', 'Commuter transit limit', 'USD per month'),
  limitRule('LIMIT-QUALIFIED-PARKING', 'Qualified parking limit', 'USD per month'),
];

export function findTaxProfile(code: string | null | undefined): TaxProfile | undefined {
  return code ? MOCK_TAX_PROFILES.find((p) => p.code === code) : undefined;
}

export function isKnownLimitRule(code: string): boolean {
  return MOCK_LIMIT_RULES.some((r) => r.code === code);
}
